import React, {useEffect, useMemo, useState, useCallback} from "react"
import {DataEmissionPanel} from "./components/data-emission/data-emission-panel"
import {Box} from "@mui/system"
import SideBarLayout from "src/v2/layout/side-bar-layout"
import {Grid, Typography, Paper} from "@mui/material"
import theme from "../../theme/theme"
import DatalakeHeader from "./components/Header"
import EmissionTabsWrappedLabel from "./components/emission-tab/emission-tab"
import {ContrapartesPanel} from "./components/emission-contrapartes/contrapartes-panel"
import DataGridTable from "src/v2/components/Table/data-grid-table"
import GenericModal from "src/v2/components/modal/generic-modal"
import {
    BoxCreditEmissionButtonGroupAdmin
} from "./components/box-admin-buttons-datalake-emission/box-admin-datalake-emission-admin-buttons"
import {EmissionSummarySections} from "./components/emission-sections/emission-summary-sections"
import {COLUMNS_MODAL_TABLE_CETIP_ISIN} from "./constants/full-list-cetip-isin.const"
import LoaderOverlay from "src/v2/components/LoaderOverlay/loader-overlay"
import Loader from "src/v2/components/Loader/loader"
import {useCetipModal} from "./hooks/use-cetip-isin-modal"
import {TokenService} from "src/services/token-service"
import EmissionEmptyState from "./components/emission-empty-state/emission-empty-state"
import {BoxDataEventFlow} from "./components/box-data-event-flow/box-data-event-flow"
import EventosFluxoService from "src/services/eventos_fluxo_service"
import SelectComponent from "src/v2/components/select/select-component"
import Documents from "./sub-page/Documents"
import RegisterDocumentService from "src/services/register-document-service"
import PropTypes from "prop-types"
import {MotorEmissaoService} from "../../../services/motor-emissao-service"
import EmissaoService from "../../../services/emissao-service"
import {useParameterMotor} from "./hooks/use-parameter-motor-price"
import {useLocation} from "react-router-dom"
import {useFluxoEventosMotor} from "./hooks/use-fluxo-eventos-motor"
import {
    EVENTOS_ORDINARIOS_COLUMNS,
    EVENTOS_EXTRAORDINARIOS_COLUMNS,
    PREMIO_COLUMNS,
    CONVERSIBILIDADE_COLUMNS,
} from "./constants/fluxo-eventos-columns.const"

const SectionPaper = ({children, sx}) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            background: theme.palette.background.paper,
            borderRadius: 0,
            ...sx,
        }}
    >
        {children}
    </Paper>
)

SectionPaper.propTypes = {
    children: PropTypes.node.isRequired,
    sx: PropTypes.object,
}

const DataLakeEmissionV2 = () => {
    const emissaoMotorService = useMemo(() => new MotorEmissaoService(), [])
    const emissaoDatalakeService = useMemo(() => new EmissaoService(), [])

    const location = useLocation()

    const tokenService = useMemo(() => new TokenService(), [])
    const eventsFluxService = useMemo(() => new EventosFluxoService(), [])
    const documentService = useMemo(() => new RegisterDocumentService(), [])

    const isAdminUser = tokenService.verifyUserIsAdmin()

    const [characterData, setCharacterData] = useState({})
    const [contraparteData, setContraparteData] = useState({})

    const [isLoadingCharacters, setIsLoadingCharacters] = useState(false)
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(false)
    const [eventFlowSelectValue, setEventFlowSelectValue] = useState("")
    const [garantiasData, setGarantiasData] = useState({})
    const [offerData, setOfferData] = useState({})
    const [fatosRelevantesData, setFatosRelevantesData] = useState([])
    const pagination = {page: 0}

    const {
        state,
        handleOpenModal,
        handleRowClick,
        callBackOnChangeInputHeader,
        selectEmission,
        dispatch,
    } = useCetipModal(emissaoMotorService)

    useEffect(() => {
        const cetipIsin = location.state?.cetip
        const rendaFixaCaracteristicasID = location.state?.id

        if (!cetipIsin || !rendaFixaCaracteristicasID) return

        selectEmission(cetipIsin, rendaFixaCaracteristicasID)
    }, [location.state?.cetip, location.state?.id, selectEmission])

    const {state: fluxoEventosState, actions: fluxoEventosActions} =
        useFluxoEventosMotor(eventsFluxService, state.selectCetipIsin)

    const fluxoEventosData = fluxoEventosState?.data?.fluxoEventos ?? {}
    const activeCaracteristicaID = fluxoEventosData.RendaFixaCaracteristicasID || state.rendaFixaCaracteristicasID

    const {state: parameterState, actions: parameterActions} = useParameterMotor(
        emissaoMotorService,
        activeCaracteristicaID
    )

    const versoesOptions = (fluxoEventosData.Versoes ?? []).map((versao) => ({
        value: String(versao),
        label: `Fluxo ${versao}`,
    }))

    const loadGarantias = useCallback(async (cetip) => {
        try {
            const response = await emissaoMotorService.getGarantias(cetip)

            setGarantiasData(
                response?.body?.data ||
                response?.body ||
                {}
            )
        } catch {
            setGarantiasData({})
        }
    }, [emissaoMotorService])

    const loadTipoOferta = useCallback(async (cetipIsin) => {
        try {
            const response = await emissaoMotorService.getTipoOferta(cetipIsin)

            setOfferData(
                response?.body?.data ||
                response?.body ||
                {}
            )
        } catch {
            setOfferData({})
        }
    }, [emissaoMotorService])

    const loadFatosRelevantes = useCallback(async (cetipIsin) => {
        try {
            const response = await emissaoMotorService.getFatosRelevantes(cetipIsin)

            const data = response?.body?.data || response?.body || []

            setFatosRelevantesData(Array.isArray(data) ? data : [])
        } catch {
            setFatosRelevantesData([])
        }
    }, [emissaoMotorService])

    const loadCharacters = useCallback(async (cetipIsin) => {
        if (!cetipIsin) return

        setIsLoadingCharacters(true)

        try {
            const request = await emissaoMotorService.getEmissaoDetalhe(cetipIsin)

            const caracteristicas = request?.body?.Caracteristicas || {}
            const contrapartes = request?.body?.Contrapartes || {}

            setCharacterData(caracteristicas)
            setContraparteData(contrapartes)

            Promise.allSettled([
                loadGarantias(cetipIsin),
                loadTipoOferta(cetipIsin),
                loadFatosRelevantes(cetipIsin),
            ])
        } catch {
            setCharacterData({})
            setContraparteData({})
            setGarantiasData({})
            setOfferData({})
            setFatosRelevantesData([])
        } finally {
            setIsLoadingCharacters(false)
        }
    }, [emissaoMotorService, loadGarantias, loadTipoOferta, loadFatosRelevantes])

    useEffect(() => {
        if (!state.selectCetipIsin) return

        const loadData = async () => {
            await Promise.allSettled([
                loadCharacters(state.selectCetipIsin),
                fluxoEventosActions.loadFluxoEventos(state.selectCetipIsin),
            ])
        }

        loadData()
    }, [state.selectCetipIsin, loadCharacters])

    useEffect(() => {
        if (fluxoEventosData.RendaFixaCaracteristicasID) {
            parameterActions.loadParametros(fluxoEventosData.RendaFixaCaracteristicasID)
        }
    }, [fluxoEventosData.RendaFixaCaracteristicasID])

    useEffect(() => {
        const versaoDocumento = fluxoEventosData.VersaoDocumento
        if (versaoDocumento === null || versaoDocumento === undefined) return
        setEventFlowSelectValue(String(versaoDocumento))
    }, [fluxoEventosData.VersaoDocumento])

    const handleChangeVersaoDocumento = async (event) => {
        const novaVersao = event.target.value
        setEventFlowSelectValue(novaVersao)
        const fluxoResponse = await fluxoEventosActions.loadFluxoEventos(state.selectCetipIsin, novaVersao)

        if (fluxoResponse?.RendaFixaCaracteristicasID) {
            parameterActions.loadParametros(fluxoResponse.RendaFixaCaracteristicasID)
        }
    }

    const isPageLoading =
        state.isLoadingModalData ||
        isLoadingCharacters ||
        isLoadingDocuments

    return (
        <SideBarLayout title="Emissão">
            {isPageLoading ? (
                <LoaderOverlay>
                    <Loader/>
                </LoaderOverlay>
            ) : (
                <GenericModal
                    isOpen={state.listFullModalVisible && !state.isLoadingModalData}
                    onClose={() => dispatch({type: "TOGGLE_MODAL"})}
                    title="Lista de CETIP/ISIN"
                    width="65%"
                >
                    <DataGridTable
                        columns={COLUMNS_MODAL_TABLE_CETIP_ISIN}
                        rows={state.rows}
                        pagination={pagination}
                        pageSize={25}
                        disableDeleteColumn
                        onRowClick={handleRowClick}
                        pointerCursor
                    />
                </GenericModal>
            )}

            <Grid
                sx={{
                    background: theme.palette.background.primary,
                    height: "100%",
                    width: "100%",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: {
                            xxxs: 0,
                            lg: 209,
                        },
                        right: 0,
                        zIndex: 0,
                    }}
                >
                    <DatalakeHeader
                        emissaoService={emissaoMotorService}
                        callBackOnChange={callBackOnChangeInputHeader}
                        handleClickFullList={handleOpenModal}
                        externalValue={state.selectCetipIsin}
                    />
                </Box>

                {!state.selectCetipIsin && Object.keys(characterData).length === 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            position: "relative",
                            mt: {xxxs: 36, md: 30, lg: 22},
                            width: "100%",
                            background: theme.palette.background.paper,
                            flexDirection: "column",
                        }}
                    >
                        <EmissionEmptyState handleClickFullList={handleOpenModal}/>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            position: "relative",
                            mt: {xxxs: 36, md: 30, lg: 22},
                            width: "100%",
                            background: theme.palette.background.paper,
                            flexDirection: "column",
                        }}
                    >
                        <Box>
                            <EmissionTabsWrappedLabel
                                emissionTabChildren={
                                    <Box
                                        sx={{
                                            width: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            background: theme.palette.background.primary,
                                        }}
                                    >
                                        <SectionPaper>
                                            <DataEmissionPanel characterData={characterData}/>
                                        </SectionPaper>

                                        <SectionPaper sx={{mt: 3}}>
                                            <ContrapartesPanel contraparteData={contraparteData}/>

                                            {isAdminUser && (
                                                <SectionPaper sx={{mt: 1}}>
                                                    <BoxCreditEmissionButtonGroupAdmin
                                                        rendaFixaCaracteristicasID={activeCaracteristicaID}
                                                    />
                                                </SectionPaper>
                                            )}

                                            <SectionPaper sx={{mt: 1}}>
                                                <EmissionSummarySections
                                                    cetipIsin={state.selectCetipIsin}
                                                    MotorEmissaoService={emissaoMotorService}
                                                    emissaoDatalakeService={emissaoDatalakeService}
                                                    characterData={characterData}
                                                    contraparteData={contraparteData}
                                                    garantiasData={garantiasData}
                                                    offerData={offerData}
                                                    fatosRelevantesData={fatosRelevantesData}
                                                    admin={isAdminUser}
                                                />
                                            </SectionPaper>
                                        </SectionPaper>
                                    </Box>
                                }
                                eventFlowTabChildren={
                                    <Box
                                        sx={{
                                            height: "100%",
                                            width: {
                                                lg: "calc(100vw - 300px)",
                                            },
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 0,
                                            p: 3,
                                            pt: 0,
                                            boxSizing: "border-box",
                                        }}
                                    >
                                        <SectionPaper>
                                            <SelectComponent
                                                label={null}
                                                value={eventFlowSelectValue}
                                                onChange={handleChangeVersaoDocumento}
                                                options={versoesOptions}
                                            />
                                        </SectionPaper>

                                        <SectionPaper>
                                            <BoxDataEventFlow
                                                loading={parameterState?.loading?.parametros ?? false}
                                                parametersData={parameterState?.data?.parametros?.Parametros?.[0] ?? {}}
                                            />
                                        </SectionPaper>

                                        <SectionPaper>
                                            <Typography variant="h3" color="common.white">
                                                Eventos Ordinários
                                            </Typography>

                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    overflowX: "auto",
                                                    width: "100%",
                                                    display: "block",
                                                    mt: 1,
                                                }}
                                            >
                                                <DataGridTable
                                                    columns={EVENTOS_ORDINARIOS_COLUMNS}
                                                    rows={(fluxoEventosData.EventosOrdinarios ?? []).map((row, index) => ({...row, __rowId: index}))}
                                                    disableDeleteColumn
                                                    getRowId={(row) => row.__rowId}
                                                    loading={fluxoEventosState?.loading?.fluxoEventos ?? false}
                                                    resetKey={`eventosOrdinarios-${eventFlowSelectValue}`}
                                                />
                                            </Box>
                                        </SectionPaper>

                                        <SectionPaper>
                                            <Typography variant="h3" color="common.white">
                                                Eventos Extraordinários
                                            </Typography>

                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    overflowX: "auto",
                                                    width: "100%",
                                                    display: "block",
                                                    mt: 1,
                                                }}
                                            >
                                                <DataGridTable
                                                    columns={EVENTOS_EXTRAORDINARIOS_COLUMNS}
                                                    rows={(fluxoEventosData.EventosExtraordinarios ?? []).map((row, index) => ({...row, __rowId: index}))}
                                                    disableDeleteColumn
                                                    getRowId={(row) => row.__rowId}
                                                    loading={fluxoEventosState?.loading?.fluxoEventos ?? false}
                                                    resetKey={`eventosExtraordinarios-${eventFlowSelectValue}`}
                                                />
                                            </Box>
                                        </SectionPaper>

                                        {(fluxoEventosData.Premio ?? []).length > 0 && (
                                            <SectionPaper>
                                                <Typography variant="h3" color="common.white">
                                                    Prêmio
                                                </Typography>

                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        overflowX: "auto",
                                                        width: "100%",
                                                        display: "block",
                                                        mt: 1,
                                                    }}
                                                >
                                                    <DataGridTable
                                                        columns={PREMIO_COLUMNS}
                                                        rows={(fluxoEventosData.Premio ?? []).map((row, index) => ({...row, __rowId: index}))}
                                                        disableDeleteColumn
                                                        getRowId={(row) => row.__rowId}
                                                        loading={fluxoEventosState?.loading?.fluxoEventos ?? false}
                                                        resetKey={`premio-${eventFlowSelectValue}`}
                                                    />
                                                </Box>
                                            </SectionPaper>
                                        )}

                                        {(fluxoEventosData.Conversibilidade ?? []).length > 0 && (
                                            <SectionPaper>
                                                <Typography variant="h3" color="common.white">
                                                    Conversibilidade
                                                </Typography>

                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        overflowX: "auto",
                                                        width: "100%",
                                                        display: "block",
                                                        mt: 1,
                                                    }}
                                                >
                                                    <DataGridTable
                                                        columns={CONVERSIBILIDADE_COLUMNS}
                                                        rows={(fluxoEventosData.Conversibilidade ?? []).map((row, index) => ({...row, __rowId: index}))}
                                                        disableDeleteColumn
                                                        getRowId={(row) => row.__rowId}
                                                        loading={fluxoEventosState?.loading?.fluxoEventos ?? false}
                                                        resetKey={`conversibilidade-${eventFlowSelectValue}`}
                                                    />
                                                </Box>
                                            </SectionPaper>
                                        )}
                                    </Box>
                                }
                                documentsChildren={
                                    <Box
                                        sx={{
                                            height: "100vh",
                                            width: {
                                                lg: "calc(100vw - 300px)",
                                            },
                                            p: 3,
                                        }}
                                    >
                                        <SectionPaper>
                                            <Documents
                                                isAdmin={isAdminUser}
                                                documentService={documentService}
                                                cetip={state.selectCetipIsin}
                                                setIsLoading={setIsLoadingDocuments}
                                            />
                                        </SectionPaper>
                                    </Box>
                                }
                            />
                        </Box>
                    </Box>
                )}
            </Grid>
        </SideBarLayout>
    )
}

export default DataLakeEmissionV2